import React, { Component, createRef } from 'react';

// CSS
import './Meetings.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';

// Properties
const properties = require('../../properties.json');

const STATUS = {
    SCHEDULED: { label: 'مجدول', severity: 'info' },
    LIVE: { label: 'مباشر الآن', severity: 'danger' },
    ENDED: { label: 'انتهى', severity: 'success' },
    CANCELLED: { label: 'ملغي', severity: 'warning' },
};

const MODES = [
    { label: 'شرح على الشاشة (سلايدات / سبورة) — الأوفر', value: 'SCREEN' },
    { label: 'كاميرا بجودة عالية', value: 'CAMERA' },
];

const DURATIONS = [
    { label: 'نص ساعة', value: 30 }, { label: '45 دقيقة', value: 45 }, { label: 'ساعة', value: 60 },
    { label: 'ساعة ونص', value: 90 }, { label: 'ساعتين', value: 120 }, { label: 'ساعتين ونص', value: 150 },
    { label: '3 ساعات', value: 180 }, { label: '4 ساعات', value: 240 },
];

function when(iso) {
    return new Date(iso).toLocaleString('ar-EG', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' });
}

// "2026-10-09T19:00" in the browser's local time, for <input type="datetime-local">
function localInput(date) {
    const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return d.toISOString().slice(0, 16);
}

function nextHour() {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    return d;
}

const EMPTY = () => ({ id: null, title: '', courseId: null, start: localInput(nextHour()), durationMinutes: 60, maxStudents: 50, mode: 'SCREEN',
                       repeat: false, weekdays: [], weeks: 8, skip: false, record: false });

// JS getDay() order; shown Saturday first like the Egyptian week
const JS_DAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const WEEKDAYS = [
    { value: 'SATURDAY', label: 'سبت' }, { value: 'SUNDAY', label: 'أحد' }, { value: 'MONDAY', label: 'اتنين' },
    { value: 'TUESDAY', label: 'تلات' }, { value: 'WEDNESDAY', label: 'أربع' }, { value: 'THURSDAY', label: 'خميس' },
    { value: 'FRIDAY', label: 'جمعة' },
];

function timeZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Cairo'; } catch (e) { return 'Africa/Cairo'; }
}

// "My meetings": book a live session on the platform's meeting server, start it, and see what's coming up.
class Meetings extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
        this.checkTimer = null;
    }

    state = {
        loading: true,
        meetings: [],
        courses: [],
        open: false,
        form: EMPTY(),
        capacity: null,
        seriesCheck: null,
        saving: false,
        expanded: {},
        attach: {},
    }

    componentDidMount() {
        document.title = properties.site_name + ' | الميتنج المباشر';
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        this.load();
        apiFetch('/courses').then(list => this.setState({ courses: (list || []).map(c => ({ label: c.title, value: c.id })) })).catch(() => {});
        this.refresh = setInterval(() => this.load(true), 60000);
    }

    componentWillUnmount() {
        clearInterval(this.refresh);
        clearTimeout(this.checkTimer);
    }

    load = (quiet) => {
        apiFetch('/meetings')
            .then(meetings => this.setState({ meetings: meetings || [], loading: false }))
            .catch(e => { this.setState({ loading: false }); if (!quiet) this.error(e); });
    }

    error = (e) => this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: e.message || String(e), life: 6000 });

    body = (form) => ({
        title: form.title,
        courseId: form.courseId || null,
        startAt: new Date(form.start).toISOString(),
        durationMinutes: form.durationMinutes,
        maxStudents: form.maxStudents,
        mode: form.mode,
        record: !!form.record,
    })

    seriesBody = (form) => ({
        meeting: this.body(form),
        weekdays: form.weekdays,
        weeks: form.weeks,
        timeZone: timeZone(),
        skipConflicts: form.skip,
    })

    startDay = (form) => JS_DAYS[new Date(form.start).getDay()];

    toggleDay = (day) => {
        const { form } = this.state;
        const has = form.weekdays.indexOf(day) >= 0;
        this.setForm({ weekdays: has ? form.weekdays.filter(d => d !== day) : form.weekdays.concat(day) });
    }

    setForm = (patch) => {
        this.setState(s => ({ form: { ...s.form, ...patch } }), this.scheduleCheck);
    }

    // asks the server whether the slot fits while the teacher fills the form
    scheduleCheck = () => {
        clearTimeout(this.checkTimer);
        this.checkTimer = setTimeout(() => {
            const { form } = this.state;
            if (!form.start || !form.maxStudents || !form.durationMinutes) return;
            if (form.repeat && !form.id) {
                apiFetch('/meetings/series/check', { method: 'POST', body: this.seriesBody({ ...form, title: form.title || '-' }) })
                    .then(seriesCheck => this.setState({ seriesCheck, capacity: null }))
                    .catch(e => this.setState({ seriesCheck: { error: e.message }, capacity: null }));
                return;
            }
            this.setState({ seriesCheck: null });
            apiFetch('/meetings/check' + (form.id ? `?excludeId=${form.id}` : ''), { method: 'POST', body: { ...this.body(form), title: form.title || '-' } })
                .then(capacity => this.setState({ capacity }))
                .catch(e => this.setState({ capacity: { fits: false, message: e.message } }));
        }, 400);
    }

    openNew = () => this.setState({ open: true, form: EMPTY(), capacity: null, seriesCheck: null }, this.scheduleCheck);

    openEdit = (m) => this.setState({
        open: true, capacity: null, seriesCheck: null,
        form: { id: m.id, title: m.title, courseId: m.courseId, start: localInput(new Date(m.startAt)),
                durationMinutes: Math.round((new Date(m.endAt) - new Date(m.startAt)) / 60000), maxStudents: m.maxStudents, mode: m.mode,
                repeat: false, weekdays: [], weeks: 8, skip: false, record: !!m.autoRecord },
    }, this.scheduleCheck);

    save = () => {
        const { form } = this.state;
        if (!form.title.trim()) { this.error(new Error('اكتب عنوان الميتنج')); return; }
        this.setState({ saving: true });
        if (form.repeat && !form.id) {
            apiFetch('/meetings/series', { method: 'POST', body: this.seriesBody(form) })
                .then(r => {
                    this.setState({ open: false, saving: false });
                    this.toast.current.show({ severity: 'success', summary: 'تم', life: 6000,
                        detail: `اتحجز ${r.created} ميعاد` + (r.skipped && r.skipped.length ? ` — و${r.skipped.length} ما اتحجزوش علشان السيرفر محجوز` : '') });
                    this.load();
                })
                .catch(e => { this.setState({ saving: false }); this.error(e); });
            return;
        }
        apiFetch(form.id ? `/meetings/${form.id}` : '/meetings', { method: form.id ? 'PUT' : 'POST', body: this.body(form) })
            .then(() => {
                this.setState({ open: false, saving: false });
                this.toast.current.show({ severity: 'success', summary: 'تم', detail: form.id ? 'تم تعديل الميتنج' : 'تم حجز الميتنج' });
                this.load();
            })
            .catch(e => { this.setState({ saving: false }); this.error(e); });
    }

    useSuggestion = () => {
        const s = this.state.capacity && this.state.capacity.suggestedStartAt;
        if (s) this.setForm({ start: localInput(new Date(s)) });
    }

    start = (m) => {
        if (m.status === 'SCHEDULED' && !window.confirm(`بدء الميتنج هيخصم ${Number(m.priceUsd).toFixed(2)} $ من محفظتك. نبدأ؟`)) return;
        window.location.href = `/meetings/${m.id}/live`;
    }

    cancelSeries = (m) => {
        if (!window.confirm(`إلغاء كل المواعيد الجاية من "${m.seriesLabel}"؟ اللي خلص أو شغال دلوقتي مش هيتأثر.`)) return;
        apiFetch(`/meetings/series/${m.seriesId}`, { method: 'DELETE' })
            .then(r => { this.toast.current.show({ severity: 'success', summary: 'تم', detail: `اتلغى ${r.cancelled} ميعاد` }); this.load(); })
            .catch(this.error);
    }

    cancel = (m) => {
        if (!window.confirm(m.status === 'LIVE' ? 'إنهاء الميتنج لكل الطلاب؟' : 'إلغاء الميتنج؟')) return;
        apiFetch(`/meetings/${m.id}`, { method: 'DELETE' }).then(() => this.load()).catch(this.error);
    }

    renderForm() {
        const { form, capacity, seriesCheck, courses, saving } = this.state;
        const series = form.repeat && !form.id;
        const conflicts = seriesCheck && seriesCheck.occurrences ? seriesCheck.occurrences.filter(o => !o.fits) : [];
        const blocked = series
            ? !seriesCheck || !!seriesCheck.error || seriesCheck.fitting === 0 || (conflicts.length > 0 && !form.skip)
            : capacity && !capacity.fits;
        const footer = (
            <div>
                <Button label="إلغاء" className="p-button-text" onClick={() => this.setState({ open: false })} />
                <Button label={form.id ? 'حفظ التعديل' : series && seriesCheck && seriesCheck.fitting ? `احجز ${form.skip ? seriesCheck.fitting : seriesCheck.total} ميعاد` : 'احجز الميتنج'}
                        icon="pi pi-check" loading={saving} disabled={saving || blocked} onClick={this.save} />
            </div>
        );
        return (
            <Dialog header={form.id ? 'تعديل الميتنج' : 'ميتنج جديد'} visible={this.state.open} style={{ width: 'min(560px, 95vw)' }}
                    footer={footer} onHide={() => this.setState({ open: false })} dir="rtl">
                <div className="mt-form">
                    <label>العنوان
                        <InputText value={form.title} maxLength={200} placeholder="مثلًا: مراجعة الباب الأول" onChange={e => this.setForm({ title: e.target.value })} />
                    </label>
                    <label>مين يقدر يحضر
                        <Dropdown value={form.courseId} options={[{ label: 'كل طلابي', value: null }, ...courses]} onChange={e => this.setForm({ courseId: e.value })} />
                    </label>
                    <div className="mt-row">
                        <label>الميعاد
                            <input type="datetime-local" className="p-inputtext" value={form.start} onChange={e => this.setForm({ start: e.target.value })} />
                        </label>
                        <label>المدة
                            <Dropdown value={form.durationMinutes} options={DURATIONS} onChange={e => this.setForm({ durationMinutes: e.value })} />
                        </label>
                    </div>
                    <div className="mt-row">
                        <label>أقصى عدد طلاب
                            <InputNumber value={form.maxStudents} min={1} max={10000} showButtons onValueChange={e => this.setForm({ maxStudents: e.value })} />
                        </label>
                        <label>نوع البث
                            <Dropdown value={form.mode} options={MODES} onChange={e => this.setForm({ mode: e.value })} />
                        </label>
                    </div>
                    <label className="mt-check">
                        <input type="checkbox" checked={!!form.record} onChange={e => this.setForm({ record: e.target.checked })} />
                        سجّل الميتنج تلقائيًا ({form.courseId ? 'التسجيل هيتضاف للكورس كفيديو مشفّر' : 'تختار الكورس بعد ما يخلص'})
                    </label>
                    {!form.id && (
                        <label className="mt-check">
                            <input type="checkbox" checked={form.repeat} onChange={e => this.setForm({ repeat: e.target.checked })} />
                            يتكرر كل أسبوع
                        </label>
                    )}
                    {series && (
                        <div className="mt-repeat">
                            <div className="mt-days">
                                {WEEKDAYS.map(d => {
                                    const isStart = d.value === this.startDay(form);
                                    const on = isStart || form.weekdays.indexOf(d.value) >= 0;
                                    return (
                                        <button type="button" key={d.value} className={'mt-day' + (on ? ' on' : '')} disabled={isStart}
                                                title={isStart ? 'يوم أول ميعاد' : ''} onClick={() => this.toggleDay(d.value)}>{d.label}</button>
                                    );
                                })}
                            </div>
                            <label>لمدة (أسبوع)
                                <InputNumber value={form.weeks} min={1} max={26} showButtons onValueChange={e => this.setForm({ weeks: e.value || 1 })} />
                            </label>
                            {seriesCheck && seriesCheck.error && <Message severity="warn" text={seriesCheck.error} />}
                            {seriesCheck && seriesCheck.occurrences && (
                                <>
                                    <div className="mt-price">
                                        <b>{seriesCheck.total}</b> ميعاد — المتاح منهم <b>{seriesCheck.fitting}</b>.
                                        كل مرة <b>{Number(seriesCheck.priceUsd).toFixed(2)} $</b> (حوالي {Number(seriesCheck.priceEgp).toLocaleString('en')} ج.م) بتتخصم لما تبدأها،
                                        والإجمالي حوالي <b>{Number(seriesCheck.totalUsd).toFixed(2)} $</b>.
                                    </div>
                                    {conflicts.length > 0 && (
                                        <div className="mt-busy">
                                            <Message severity="warn" text={`${conflicts.length} مواعيد مش متاحة:`} />
                                            <ul className="mt-conflicts">
                                                {conflicts.slice(0, 8).map(o => (
                                                    <li key={o.startAt}>{when(o.startAt)} — {o.message}{o.suggestedStartAt ? ` (أقرب ميعاد: ${when(o.suggestedStartAt)})` : ''}</li>
                                                ))}
                                            </ul>
                                            <label className="mt-check">
                                                <input type="checkbox" checked={form.skip} onChange={e => this.setForm({ skip: e.target.checked })} />
                                                احجز المتاح بس وسيب المحجوز (تقدر تحجزهم لوحدهم بعدين)
                                            </label>
                                        </div>
                                    )}
                                    {conflicts.length === 0 && <Message severity="success" text="كل المواعيد متاحة على السيرفر" />}
                                </>
                            )}
                        </div>
                    )}
                    <p className="mt-hint">الطلاب بيتفرجوا ويسمعوا ويكتبوا في الشات ويرفعوا إيدهم. العدد ده هو أقصى عدد يقدر يدخل الميتنج،
                        وعليه بيتحسب السعر: بيتخصم من محفظتك لما تبدأ الميتنج، وأي وقت زيادة بعد الميعاد بيتحسب كل ربع ساعة.</p>
                    {!series && capacity && capacity.priceUsd != null && (
                        <div className="mt-price">التكلفة: <b>{Number(capacity.priceUsd).toFixed(2)} $</b>
                            {capacity.priceEgp != null && <span> (حوالي {Number(capacity.priceEgp).toLocaleString('en')} ج.م)</span>}
                            <span className="mt-hint"> — بتتخصم لما تبدأ الميتنج</span></div>
                    )}
                    {!series && capacity && capacity.fits && <Message severity="success" text="الميعاد متاح على السيرفر" />}
                    {!series && capacity && !capacity.fits && (
                        <div className="mt-busy">
                            <Message severity="warn" text={capacity.message || 'الميعاد مش متاح'} />
                            {capacity.suggestedStartAt && (
                                <Button className="p-button-sm p-button-outlined" label={'استخدم أقرب ميعاد: ' + when(capacity.suggestedStartAt)} onClick={this.useSuggestion} />
                            )}
                        </div>
                    )}
                </div>
            </Dialog>
        );
    }

    attachRecording = (m) => {
        const courseId = this.state.attach[m.id];
        if (!courseId) { this.error(new Error('اختار الكورس')); return; }
        apiFetch(`/meetings/${m.id}/recording/attach`, { method: 'POST', body: { courseId } })
            .then(() => { this.toast.current.show({ severity: 'success', summary: 'تم', detail: 'التسجيل اتضاف للكورس وبيتشفّر' }); this.load(); })
            .catch(this.error);
    }

    renderRecording(m) {
        if (!m.recordingStatus) {
            return m.autoRecord && m.status === 'SCHEDULED' ? <div className="mt-rec">⏺ هيتسجل تلقائيًا</div> : null;
        }
        const text = {
            RECORDING: '⏺ بيتسجل دلوقتي',
            PROCESSING: '⏳ التسجيل بيتجهز...',
            ATTACHED: '✓ التسجيل اتضاف للكورس كفيديو (بيتشفّر زي أي فيديو)',
            FAILED: '✗ التسجيل ما اكتملش',
        }[m.recordingStatus];
        if (m.recordingStatus === 'READY') {
            return (
                <div className="mt-rec mt-attach">
                    <span>التسجيل جاهز — اختار الكورس اللي يتضاف ليه:</span>
                    <Dropdown value={this.state.attach[m.id] || null} options={this.state.courses} placeholder="الكورس"
                              onChange={e => this.setState(s => ({ attach: { ...s.attach, [m.id]: e.value } }))} />
                    <Button label="أضفه" icon="pi pi-plus" className="p-button-sm" onClick={() => this.attachRecording(m)} />
                </div>
            );
        }
        return <div className={'mt-rec' + (m.recordingStatus === 'FAILED' ? ' failed' : '')}>{text || m.recordingStatus}</div>;
    }

    renderMeeting(m) {
        const st = STATUS[m.status] || { label: m.status };
        const past = m.status === 'SCHEDULED' && new Date(m.endAt) < new Date();
        return (
            <Card key={m.id} className={'mt-card' + (m.status === 'LIVE' ? ' live' : '')}>
                <div className="mt-card-head">
                    <h3>{m.title}</h3>
                    <span className="mt-tags">
                        {m.seriesLabel && <Tag value={'🔁 ' + m.seriesLabel} severity="info" />}
                        <Tag value={past ? 'فات ميعاده' : st.label} severity={past ? 'warning' : st.severity} />
                    </span>
                </div>
                <div className="mt-meta">
                    <span><i className="pi pi-calendar" /> {when(m.startAt)}</span>
                    <span><i className="pi pi-clock" /> {Math.round((new Date(m.endAt) - new Date(m.startAt)) / 60000)} دقيقة</span>
                    <span><i className="pi pi-users" /> حتى {m.maxStudents} طالب</span>
                    <span><i className={m.mode === 'SCREEN' ? 'pi pi-desktop' : 'pi pi-video'} /> {m.mode === 'SCREEN' ? 'شاشة' : `كاميرا ${m.videoHeight}p`}</span>
                    <span><i className="pi pi-book" /> {m.courseTitle || 'كل الطلاب'}</span>
                    {m.chargedUsd != null
                        ? <span><i className="pi pi-wallet" /> اتخصم {Number(m.chargedUsd).toFixed(2)} $</span>
                        : m.priceUsd != null && (m.status === 'SCHEDULED') && <span><i className="pi pi-wallet" /> {Number(m.priceUsd).toFixed(2)} $ عند البدء</span>}
                </div>
                {this.renderRecording(m)}
                {(m.status === 'LIVE' || m.status === 'ENDED') && (
                    <div className="mt-actions mt-report">
                        <Button label="تقرير الحضور" icon="pi pi-users" className="p-button-outlined p-button-sm"
                                onClick={() => { window.location.href = `/meetings/${m.id}/attendance`; }} />
                    </div>
                )}
                {(m.status === 'SCHEDULED' || m.status === 'LIVE') && !past && (
                    <div className="mt-actions">
                        <Button label={m.status === 'LIVE' ? 'ارجع للميتنج' : 'ابدأ الميتنج'} icon="pi pi-play" className="p-button-danger"
                                disabled={!m.actionable} tooltip={!m.actionable ? 'تقدر تبدأ قبل الميعاد بربع ساعة' : null}
                                onClick={() => this.start(m)} />
                        {m.status === 'SCHEDULED' && <Button label="تعديل" icon="pi pi-pencil" className="p-button-outlined" onClick={() => this.openEdit(m)} />}
                        <Button label={m.status === 'LIVE' ? 'إنهاء' : 'إلغاء'} icon="pi pi-times" className="p-button-text p-button-danger" onClick={() => this.cancel(m)} />
                    </div>
                )}
            </Card>
        );
    }

    // upcoming meetings with each series folded into its next occurrence (the rest kept in order behind it)
    groupSeries(list) {
        const groups = [];
        const bySeries = {};
        list.forEach(m => {
            if (m.seriesId && m.status !== 'LIVE') {
                if (!bySeries[m.seriesId]) { bySeries[m.seriesId] = [m]; groups.push(bySeries[m.seriesId]); } else bySeries[m.seriesId].push(m);
            } else {
                groups.push([m]);
            }
        });
        return groups;
    }

    render() {
        const { loading, meetings } = this.state;
        const upcoming = meetings.filter(m => m.status === 'LIVE' || (m.status === 'SCHEDULED' && new Date(m.endAt) >= new Date()));
        const past = meetings.filter(m => upcoming.indexOf(m) < 0).reverse();
        return (
            <>
                <Header pageName="الميتنج المباشر" />
                <Toast ref={this.toast}></Toast>
                <div className="content meetings-page" dir="rtl">
                    <div className="container">
                        <div className="mt-top">
                            <h2>الميتنج المباشر</h2>
                            <Button label="ميتنج جديد" icon="pi pi-plus" onClick={this.openNew} />
                        </div>
                        {loading && <p className="mt-hint">جاري التحميل...</p>}
                        {!loading && upcoming.length === 0 && <p className="mt-hint">مفيش ميتنجات جاية. احجز أول ميتنج من زرار "ميتنج جديد".</p>}
                        {this.groupSeries(upcoming).map(g => (
                            <React.Fragment key={g[0].id}>
                                {this.renderMeeting(g[0])}
                                {g[0].seriesId && g.length > 1 && (
                                    <div className="mt-series-bar">
                                        <span>🔁 {g[0].seriesLabel} — فيه كمان {g.length - 1} ميعاد جاي</span>
                                        <Button className="p-button-text p-button-sm" label={this.state.expanded[g[0].seriesId] ? 'إخفاء المواعيد' : 'عرض كل المواعيد'}
                                                onClick={() => this.setState(s => ({ expanded: { ...s.expanded, [g[0].seriesId]: !s.expanded[g[0].seriesId] } }))} />
                                        <Button className="p-button-text p-button-sm p-button-danger" label="إلغاء كل المواعيد الجاية" onClick={() => this.cancelSeries(g[0])} />
                                    </div>
                                )}
                                {g[0].seriesId && this.state.expanded[g[0].seriesId] && g.slice(1).map(m => this.renderMeeting(m))}
                            </React.Fragment>
                        ))}
                        {past.length > 0 && <h3 className="mt-sub">آخر أسبوع</h3>}
                        {past.map(m => this.renderMeeting(m))}
                    </div>
                </div>
                {this.renderForm()}
            </>
        );
    }
}

export default Meetings;
